import {
  ColumnType,
  SelectLabelValueType,
  UserFilterStateType,
  UserListType,
  UsersListType,
  UserType,
} from "@/common/types";
import Table from "../../components/table";
import { useEffect, useRef, useState } from "react";
import { Expand, RotateCcw } from "lucide-react";

import UserSheet from "./userSheet";
import DetailedView from "./detailedView";
import {
  getAllDoctors,
  getInvitees,
  getUsers,
} from "@/redux/actions/usersActions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducers";
import { capitalize, isUserAdmin } from "@/utils/common.util";
import CustomSelect from "@/components/select";
import AutoComplete from "@/components/autocomplete";
import useDebounce from "@/hooks/use-debounce";
import DeleteInvitedUser from "./delete-invited-user";
import { convertDateToString } from "@/utils/date.util";
import CardsForTable from "@/components/cards-for-table";

const roles = [
  { value: "admin", label: "Admin" },
  { value: "doctor", label: "Doctor" },
  { value: "patient", label: "Patient" },
];

const status = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
];

const filterStateInitial = {
  role: { value: "", label: "" },
  doctor: "",
};

const Users = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: any = useDispatch();
  const rowsPerPage = 10;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { usersList, doctors } = useSelector((state: RootState) => state.users);
  const [detailedView, setDetailedView] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserType>({
    name: "",
    email: "",
    role: "",
    doctor: null,
    created_at: "",
  });
  const [filterState, setFilterState] =
    useState<UserFilterStateType>(filterStateInitial);
  const [filteredUsers, setFilteredUsers] = useState<UserType[]>([]);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState<string>("");
  const [inviteeStatus, setInviteeStatus] = useState<SelectLabelValueType>({
    value: "active",
    label: "Active",
  });

  const [currentPage, setCurrentPage] = useState<number>(1);

  const debouncedDoctorSearchTerm = useDebounce(doctorSearchTerm, 500);

  useEffect(() => {
    if (inviteeStatus.value === "active") {
      dispatch(getUsers(filterState, currentPage, rowsPerPage));
    }
  }, [currentPage, filterState]);

  useEffect(() => {
    dispatch(getAllDoctors(debouncedDoctorSearchTerm));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDoctorSearchTerm]);

  useEffect(() => {
    if (isUserAdmin()) {
      setFilteredUsers((usersList as UsersListType)?.results || []);
    } else {
      setFilteredUsers(
        (usersList as UsersListType)?.results?.map((user) => {
          if (user.role === "admin") {
            return {
              ...user,
              email: "-",
            };
          } else {
            return user;
          }
        }) || []
      );
    }
  }, [usersList]);

  const toggleDetailedView = () => {
    setDetailedView(!detailedView);
  };

  const openDetailedView = (user: UserType) => {
    setSelectedUser(user);
    toggleDetailedView();
  };

  const clearSelectedUser = () => {
    setSelectedUser({
      name: "",
      email: "",
      role: "",
      doctor: null,
      created_at: "",
    });
    setInviteeStatus({ value: "active", label: "Active" });
  };

  const resetCurrentPage = () => {
    setCurrentPage(1);
  };

  const closeExpandView = () => {
    clearSelectedUser();
    toggleDetailedView();
  };

  const handleSelect = (key: string, value: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    resetCurrentPage();
  };

  const handleInviteeStatus = (option: any) => {
    setInviteeStatus(option);
    setFilterState(filterStateInitial);
    if (option.value === "active") {
      dispatch(getUsers(filterStateInitial, currentPage, rowsPerPage));
    } else {
      dispatch(getInvitees());
    }
  };

  const resetFilter = () => {
    setFilterState(filterStateInitial);
    handleInviteeStatus(status[0]);
    resetCurrentPage();
  };

  const columns: ColumnType[] = [
    { field: "name", headerName: "Name" },
    {
      field: "email",
      headerName: "Email",
    },
    {
      field: "role",
      headerName: "Role",
      render: (row) => capitalize((row as UserType).role),
    },
    {
      field: "created_at",
      headerName: "Created At",
      render: (row) => convertDateToString((row as UserType).created_at),
    },
    {
      field: "",
      headerName: "Action",
      render: (row) => (
        <div className="flex gap-6 items-center cursor-pointer">
          <Expand onClick={() => openDetailedView(row as UserType)} />
        </div>
      ),
    },
  ];

  const getColumns = () => {
    const filteredColumns: ColumnType[] = [];

    if (inviteeStatus.value === "active") {
      filteredColumns.push(...columns);
    } else {
      ["name", "email", "role"].forEach((column) => {
        const columnToPush = columns.find((col) => col.field === column);
        if (columnToPush !== undefined) {
          filteredColumns.push(columnToPush);
        }
      });
      filteredColumns.push({
        field: "",
        headerName: "Action",
        render: (row) => (
          <div className="flex gap-6 items-center cursor-pointer">
            <DeleteInvitedUser userId={row.id as number} />
          </div>
        ),
      });
    }

    return filteredColumns;
  };

  const getColumnsForCards = () => {
    let columns = getColumns();
    return columns.filter((el) => el.headerName !== "Action");
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <div className="px-4 md:px-8 py-2">
      {!detailedView && (
        <div className="flex items-center justify-between mb-4 mt-2">
          <div className="flex items-center gap-2 flex-wrap">
            {isUserAdmin() && (
              <div className="w-[150px] md:w-[175px]">
                <CustomSelect
                  value={inviteeStatus.value}
                  options={status}
                  onChange={(option) => handleInviteeStatus(option)}
                  placeholder="status"
                  labelKey="label"
                  valueKey="value"
                />
              </div>
            )}
            {isUserAdmin() && inviteeStatus.value === "active" && (
              <div className="w-[150px] md:w-[175px]">
                <CustomSelect
                  value={filterState?.role?.value}
                  options={roles}
                  onChange={(option) => handleSelect("role", option)}
                  placeholder="role"
                  labelKey="label"
                  valueKey="value"
                />
              </div>
            )}
            {filterState?.role?.value === "patient" && (
              <div className="w-[150px] md:w-[175px]">
                <AutoComplete
                  placeHolder="doctor"
                  options={doctors as UserType[]}
                  labelKey="name"
                  onSelect={(option) => handleSelect("doctor", option?.id)}
                  onChange={(search) => setDoctorSearchTerm(search)}
                  listCount={5}
                />
              </div>
            )}
            {isUserAdmin() && (
              <RotateCcw
                className="cursor-pointer w-[40px] h-[40px] border border-black px-[10px] rounded-md"
                onClick={resetFilter}
              />
            )}
          </div>
          <div className="hidden md:block">
            {!detailedView && (
              <UserSheet
                buttonRef={buttonRef}
                selectedUser={selectedUser}
                onClose={clearSelectedUser}
              />
            )}
          </div>
        </div>
      )}

      <div className="block md:hidden mb-4">
        {!detailedView && (
          <UserSheet
            buttonRef={buttonRef}
            selectedUser={selectedUser}
            onClose={clearSelectedUser}
          />
        )}
      </div>

      {/* Desktop View */}
      {!detailedView && (
        <div className="hidden md:flex items-center justify-center">
          <Table
            rows={filteredUsers || []}
            columns={getColumns()}
            rowsPerPage={rowsPerPage}
            selectedPage={currentPage}
            totalPages={
              Math.ceil((usersList as UserListType)?.count / rowsPerPage) || 0
            }
            showPrevious={(usersList as UserListType)?.previous !== null}
            showNext={(usersList as UserListType)?.next !== null}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Mobile View */}
      {!detailedView && (
        <div className="flex md:hidden">
          <CardsForTable
            rows={filteredUsers || []}
            columns={getColumnsForCards()}
            rowsPerPage={rowsPerPage}
            selectedPage={currentPage}
            totalPages={
              Math.ceil((usersList as UserListType)?.count / rowsPerPage) || 0
            }
            showPrevious={(usersList as UserListType)?.previous !== null}
            showNext={(usersList as UserListType)?.next !== null}
            onPageChange={handlePageChange}
            onRowClick={(e, row) => {
              e.preventDefault();
              openDetailedView(row as UserType);
            }}
          />
        </div>
      )}

      {detailedView && (
        <DetailedView
          users={filteredUsers}
          selectedUser={selectedUser}
          onClose={closeExpandView}
        />
      )}
    </div>
  );
};

export default Users;
